import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaPlus, FaCheck, FaTimes, FaEdit, FaTrashAlt, FaTag, FaHistory } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { useCurrency } from '../../../contexts/CurrencyContext';

const PageContainer = styled.div`
  padding: 40px;
  min-height: 100vh;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const AddButton = styled.button`
  background: #34574A;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #27443a;
  }
`;

const TableWrapper = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 700px;
  background: #fff;
  border-radius: 12px;

  th, td {
    padding: 16px 12px;
    text-align: left;
    font-size: 0.95rem;
    border-bottom: 1px solid #eee;
    vertical-align: top;
  }

  td {
    padding: 12px 14px;
    border-bottom: 1px solid #f1f1f1;
    color: #333;
    &.price {
      white-space: nowrap;
    }
  }

  th {
    background: #f8f9fa;
    color: #666;
    font-weight: 600;
    text-transform: uppercase;
  }

  tr:hover {
    background: #f8f9fa;
  }
`;

const StatusButton = styled.button`
  background: ${props => props.$ativo ? '#22c55e' : '#c62828'};
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  padding: 4px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s;
  &:hover {
    opacity: 0.8;
  }
`;

const ActionCell = styled.td`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  color: #c0843d;
  font-size: 1.1rem;
  transition: background 0.2s;
  &:hover {
    background: #f8f8f8;
    color: #c62828;
  }
`;

const TabButton = styled.button`
  padding: 12px 24px;
  background: ${props => props.$active ? '#34574A' : '#f5f5f5'};
  color: ${props => props.$active ? '#fff' : '#666'};
  border: none;
  border-radius: 8px 8px 0 0;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background: ${props => props.$active ? '#34574A' : '#e8e8e8'};
  }
`;

const TabContent = styled.div`
  display: ${props => props.$active ? 'block' : 'none'};
`;

const CouponTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;

  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #eee;
    font-size: 0.9rem;
  }

  th {
    background: #f8f9fa;
    font-weight: 600;
    color: #666;
  }

  tr:hover {
    background: #f8f9fa;
  }
`;

const CouponForm = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FormLabel = styled.label`
  font-weight: 600;
  color: #34574A;
  font-size: 0.9rem;
`;

const FormInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.95rem;
  &:focus {
    outline: none;
    border-color: #34574A;
  }
`;

const FormSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.95rem;
  background: white;
`;

const CouponBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => props.$ativo ? '#22c55e' : '#c62828'};
  color: white;
`;

const UsageCounter = styled.span`
  font-size: 0.85rem;
  color: #666;
`;

const CouponManager = ({ planoId, cupons: initialCupons, onCouponChange }) => {
  const [cupons, setCupons] = useState(initialCupons || []);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editCoupon, setEditCoupon] = useState(null);
  const [showHistory, setShowHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [usos, setUsos] = useState([]);

  const [codigo, setCodigo] = useState('');
  const [desconto, setDesconto] = useState('');
  const [tipoDesconto, setTipoDesconto] = useState('percentual');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [usoMinimo, setUsoMinimo] = useState('0');
  const [usoMaximo, setUsoMaximo] = useState('100');
  const [usuario, setUsuario] = useState('');
  const [ativo, setAtivo] = useState(true);

  const fetchUsos = async (cupomId) => {
    setLoadingHistory(true);
    const token = localStorage.getItem('auth_token');
    try {
      const res = await fetch(`${import.meta.env.VITE_URL_API}/cupons/${cupomId}/usos`, {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      setUsos(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erro ao buscar usos:', err);
    }
    setLoadingHistory(false);
  };

  useEffect(() => {
    setCupons(initialCupons || []);
  }, [initialCupons]);

  const handleAdd = () => {
    setEditCoupon(null);
    setCodigo('');
    setDesconto('');
    setTipoDesconto('percentual');
    setDataInicio('');
    setDataFim('');
    setUsoMinimo('0');
    setUsoMaximo('100');
    setUsuario('');
    setAtivo(true);
    setShowForm(true);
  };

  const handleEdit = (cupom) => {
    setEditCoupon(cupom);
    setCodigo(cupom.codigoCupomSTR || '');
    setDesconto(cupom.descontoCUPOM || '');
    setTipoDesconto(cupom.tipoDescontoCUPOM || 'percentual');
    setDataInicio(cupom.dataInicioCUPOM?.split('T')[0] || '');
    setDataFim(cupom.dataFimCUPOM?.split('T')[0] || '');
    setUsoMinimo(cupom.usoMinimoCUPOM || '0');
    setUsoMaximo(cupom.usoMaximoCUPOM || '100');
    setUsuario(cupom.codigoUSUARIO || '');
    setAtivo(cupom.ativoCUPOM !== false);
    setShowForm(true);
  };

  const handleDelete = async (cupom) => {
    Swal.fire({
      title: 'Tem certeza?',
      text: `Deseja excluir o cupom "${cupom.codigoCupomSTR}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#c62828',
      cancelButtonColor: '#34574A',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {
        const token = localStorage.getItem('auth_token');
        try {
          await fetch(`${import.meta.env.VITE_URL_API}/cupons/${cupom.codigoCUPOM}`, {
            method: 'DELETE',
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
          Swal.fire('Sucesso', 'Cupom excluído com sucesso!', 'success');
          setCupons(cupons.filter(c => c.codigoCUPOM !== cupom.codigoCUPOM));
          if (onCouponChange) onCouponChange();
        } catch {
          Swal.fire('Erro', 'Não foi possível excluir o cupom.', 'error');
        }
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validations
    if (!codigo || codigo.trim() === '') {
      Swal.fire('Erro', 'O código do cupom é obrigatório.', 'error');
      return;
    }
    if (!desconto || Number(desconto) <= 0) {
      Swal.fire('Erro', 'O desconto deve ser maior que zero.', 'error');
      return;
    }
    if (!dataInicio || !dataFim) {
      Swal.fire('Erro', 'As datas de início e fim são obrigatórias.', 'error');
      return;
    }
    if (new Date(dataInicio) > new Date(dataFim)) {
      Swal.fire('Erro', 'A data de início deve ser anterior à data de fim.', 'error');
      return;
    }
    if (Number(usoMaximo) <= 0) {
      Swal.fire('Erro', 'O uso máximo deve ser maior que zero.', 'error');
      return;
    }
    if (Number(usoMinimo) > Number(usoMaximo)) {
      Swal.fire('Erro', 'O uso mínimo não pode ser maior que o uso máximo.', 'error');
      return;
    }
    if (tipoDesconto === 'percentual' && Number(desconto) > 100) {
      Swal.fire('Erro', 'O desconto percentual não pode ser maior que 100%.', 'error');
      return;
    }

    setSubmitting(true);
    const token = localStorage.getItem('auth_token');
    
    const payload = {
      codigoPLANO: planoId,
      codigoUSUARIO: usuario || null,
      codigoCupomSTR: codigo.toUpperCase().trim(),
      descontoCUPOM: Number(desconto),
      tipoDescontoCUPOM: tipoDesconto,
      dataInicioCUPOM: dataInicio,
      dataFimCUPOM: dataFim,
      usoMinimoCUPOM: Number(usoMinimo),
      usoMaximoCUPOM: Number(usoMaximo),
      ativoCUPOM: ativo
    };

    try {
      if (editCoupon) {
        await fetch(`${import.meta.env.VITE_URL_API}/cupons/${editCoupon.codigoCUPOM}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });
        Swal.fire('Sucesso', 'Cupom atualizado com sucesso!', 'success');
        setCupons(cupons.map(c => c.codigoCUPOM === editCoupon.codigoCUPOM ? { ...c, ...payload } : c));
      } else {
        const res = await fetch(`${import.meta.env.VITE_URL_API}/cupons`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });
        const newCoupon = await res.json();
        Swal.fire('Sucesso', 'Cupom criado com sucesso!', 'success');
        setCupons([...cupons, newCoupon]);
      }
      setShowForm(false);
      if (onCouponChange) onCouponChange();
    } catch {
      Swal.fire('Erro', 'Não foi possível salvar o cupom.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewHistory = (cupom) => {
    setShowHistory(cupom);
    fetchUsos(cupom.codigoCUPOM);
  };

  if (showHistory) {
    return (
      <div style={{ padding: '20px 0' }}>
        <button 
          onClick={() => setShowHistory(null)}
          style={{ 
            background: '#34574A', 
            color: '#fff', 
            border: 'none', 
            borderRadius: 6, 
            padding: '8px 16px', 
            cursor: 'pointer',
            marginBottom: 16 
          }}
        >
          ← Voltar
        </button>
        <h3 style={{ marginBottom: 16 }}>Histórico de Uso - {showHistory.codigoCupomSTR}</h3>
        {loadingHistory ? (
          <p style={{ textAlign: 'center', color: '#666' }}>Carregando histórico...</p>
        ) : usos.length === 0 ? (
          <p style={{ color: '#666' }}>Nenhum uso registrado para este cupom.</p>
        ) : (
          <CouponTable>
            <thead>
              <tr>
                <th>Data</th>
                <th>Usuário</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {usos.map((uso, idx) => (
                <tr key={idx}>
                  <td>{new Date(uso.dataUSO || uso.createdAt).toLocaleString('pt-BR')}</td>
                  <td>{uso.usuario?.nome || uso.codigoUSUARIO || '-'}</td>
                  <td>
                    <CouponBadge $ativo={true}>Utilizado</CouponBadge>
                  </td>
                </tr>
              ))}
            </tbody>
          </CouponTable>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Cupons do Plano</h3>
        <button 
          onClick={handleAdd}
          style={{ 
            background: '#34574A', 
            color: '#fff', 
            border: 'none', 
            borderRadius: 8, 
            padding: '10px 20px', 
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <FaPlus /> Novo Cupom
        </button>
      </div>

      {showForm && (
        <CouponForm onSubmit={handleSubmit}>
          <FormField>
            <FormLabel>Código do Cupom</FormLabel>
            <FormInput 
              value={codigo} 
              onChange={e => setCodigo(e.target.value.toUpperCase())}
              placeholder="Ex: PROMO10"
              required
            />
          </FormField>
          <FormField>
            <FormLabel>Desconto</FormLabel>
            <FormInput 
              type="number"
              value={desconto} 
              onChange={e => setDesconto(e.target.value)}
              placeholder="Ex: 10"
              required
            />
          </FormField>
          <FormField>
            <FormLabel>Tipo de Desconto</FormLabel>
            <FormSelect value={tipoDesconto} onChange={e => setTipoDesconto(e.target.value)}>
              <option value="percentual">Percentual (%)</option>
              <option value="valor">Valor Fixo (€)</option>
            </FormSelect>
          </FormField>
          <FormField>
            <FormLabel>Usuário (opcional)</FormLabel>
            <FormInput 
              type="number"
              value={usuario} 
              onChange={e => setUsuario(e.target.value)}
              placeholder="ID do usuário para cupom exclusivo"
            />
          </FormField>
          <FormField>
            <FormLabel>Data Início</FormLabel>
            <FormInput 
              type="date"
              value={dataInicio} 
              onChange={e => setDataInicio(e.target.value)}
              required
            />
          </FormField>
          <FormField>
            <FormLabel>Data Fim</FormLabel>
            <FormInput 
              type="date"
              value={dataFim} 
              onChange={e => setDataFim(e.target.value)}
              required
            />
          </FormField>
          <FormField>
            <FormLabel>Uso Mínimo</FormLabel>
            <FormInput 
              type="number"
              value={usoMinimo} 
              onChange={e => setUsoMinimo(e.target.value)}
              min="0"
            />
          </FormField>
          <FormField>
            <FormLabel>Uso Máximo</FormLabel>
            <FormInput 
              type="number"
              value={usoMaximo} 
              onChange={e => setUsoMaximo(e.target.value)}
              min="1"
              required
            />
          </FormField>
          <FormField style={{ gridColumn: '1/3' }}>
            <FormLabel>Status</FormLabel>
            <div style={{ display: 'flex', gap: 12 }}>
              <StatusButton type="button" $ativo={ativo} onClick={() => setAtivo(true)}>Ativo</StatusButton>
              <StatusButton type="button" $ativo={!ativo} onClick={() => setAtivo(false)}>Inativo</StatusButton>
            </div>
          </FormField>
          <div style={{ gridColumn: '1/3', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <button 
              type="button" 
              onClick={() => setShowForm(false)}
              disabled={submitting}
              style={{ 
                background: '#bbb', 
                color: '#222', 
                border: 'none', 
                borderRadius: 8, 
                padding: '10px 18px', 
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1
              }}
            >
              Cancelar
            </button>
            <button 
              type="submit"
              disabled={submitting}
              style={{ 
                background: '#34574A', 
                color: '#fff', 
                border: 'none', 
                borderRadius: 8, 
                padding: '10px 18px', 
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              {submitting ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </CouponForm>
      )}

      {loading ? (
        <p style={{ textAlign: 'center', color: '#666' }}>Carregando cupons...</p>
      ) : cupons.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#666', padding: '40px 0' }}>
          Nenhum cupom cadastrado para este plano.
        </p>
      ) : (
        <CouponTable>
          <thead>
            <tr>
              <th>Código</th>
              <th>Desconto</th>
              <th>Tipo</th>
              <th>Data Início</th>
              <th>Data Fim</th>
              <th>Usos</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {cupons.map(cupom => (
              <tr key={cupom.codigoCUPOM}>
                <td style={{ fontWeight: 600, color: '#34574A' }}>{cupom.codigoCupomSTR}</td>
                <td>
                  {cupom.tipoDescontoCUPOM === 'percentual' 
                    ? `${cupom.descontoCUPOM}%` 
                    : `€${Number(cupom.descontoCUPOM).toFixed(2)}`}
                </td>
                <td style={{ textTransform: 'capitalize' }}>{cupom.tipoDescontoCUPOM}</td>
                <td>{new Date(cupom.dataInicioCUPOM).toLocaleDateString('pt-BR')}</td>
                <td>{new Date(cupom.dataFimCUPOM).toLocaleDateString('pt-BR')}</td>
                <td>
                  <UsageCounter>
                    {cupom.usoAtualCUPOM || 0} / {cupom.usoMaximoCUPOM}
                  </UsageCounter>
                </td>
                <td>
                  <CouponBadge $ativo={cupom.ativoCUPOM}>
                    {cupom.ativoCUPOM ? 'Ativo' : 'Inativo'}
                  </CouponBadge>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <IconButton 
                      title="Ver histórico" 
                      onClick={() => handleViewHistory(cupom)}
                    >
                      <FaHistory />
                    </IconButton>
                    <IconButton 
                      title="Editar" 
                      onClick={() => handleEdit(cupom)}
                    >
                      <FaEdit />
                    </IconButton>
                    <IconButton 
                      title="Excluir" 
                      onClick={() => handleDelete(cupom)}
                    >
                      <FaTrashAlt />
                    </IconButton>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </CouponTable>
      )}
    </div>
  );
};

const PlanoForm = ({ onSubmit, onCancel, initialData, tiposPlanos, activeTab, setActiveTab, planoId, onCouponChange }) => {
  const [nome, setNome] = useState(initialData?.nomePLANO || '');
  const [descricao, setDescricao] = useState(initialData?.descricaoPLANO || initialData?.descricao || '');
  const [preco, setPreco] = useState(initialData?.precoPLANO || initialData?.valorPLANO || '');
  const [promo, setPromo] = useState(initialData?.promoPLANO || '');
  const [duracao, setDuracao] = useState(initialData?.duracaoPLANO || initialData?.tempoPLANO || '');
  const [ativo, setAtivo] = useState(initialData?.ativoPLANO !== false);
  const [tipo, setTipo] = useState(initialData?.tipoPLANO || '');
  const [slug, setSlug] = useState(initialData?.slugPLANO || '');
  const [quantidade, setQuantidade] = useState(initialData?.quantidadePLANO || '');
  const [url, setUrl] = useState(initialData?.urlPLANO || '');
  const [destacar, setDestacar] = useState(initialData?.destacarPLANO || 0);
  const [destaque, setDestaque] = useState(initialData?.destaquePLANO || 0);
  const [stripeid, setStripeid] = useState(initialData?.stripeidPLANO || '');
  const [moloniid, setMoloniid] = useState(initialData?.moloniidPLANO || '');
  const [ordem, setOrdem] = useState(initialData?.ordemPLANO || '');
  const [descritivos, setDescritivos] = useState(Array.isArray(initialData?.descritivosPLANO) ? initialData.descritivosPLANO : (initialData?.descritivosPLANO ? Object.values(initialData.descritivosPLANO) : []));
  // Drag-and-drop para reordenar descritivos
  const [dragIndex, setDragIndex] = useState(null);
  const handleDragStart = (idx) => setDragIndex(idx);
  const handleDragOver = (idx, e) => {
    e.preventDefault(); // necessário para permitir drop
  };
  const handleDrop = (idx) => {
    if (dragIndex === null || dragIndex === idx) return;
    setDescritivos((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(dragIndex, 1);
      arr.splice(idx, 0, moved);
      return arr;
    });
    setDragIndex(null);
  };

  const handleAddDescritivo = () => setDescritivos([...descritivos, '']);
  const handleRemoveDescritivo = idx => setDescritivos(descritivos.filter((_, i) => i !== idx));
  const handleChangeDescritivo = (idx, value) => setDescritivos(descritivos.map((d, i) => i === idx ? value : d));

  const handleSubmit = e => {
    e.preventDefault();
    onSubmit({ nome, descricao, preco, promo, duracao, ativo, tipo, slug, quantidade, url, destacar, destaque, stripeid, moloniid, ordem, descritivosPLANO: descritivos });
  };

  return (
    <div style={{ minWidth: 600, maxWidth: 900, width: '100%' }}>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 0 }}>
        <TabButton $active={activeTab === 'plano'} onClick={() => setActiveTab('plano')}>
          Dados do Plano
        </TabButton>
        <TabButton $active={activeTab === 'cupons'} onClick={() => setActiveTab('cupons')} disabled={!initialData}>
          Cupons {initialData && <FaTag style={{ marginLeft: 6, fontSize: '0.9rem' }} />}
        </TabButton>
      </div>

      {/* Tab Content: Plano */}
      <TabContent $active={activeTab === 'plano'}>
        <form onSubmit={handleSubmit} style={{
          display:'grid',
          gridTemplateColumns:'1fr 1fr',
          gap:20,
          padding: '24px 0',
        }}>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            <label style={{fontWeight:600,color:'#34574A'}}>Nome</label>
            <input value={nome} onChange={e=>setNome(e.target.value)} required style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:6,fontSize:'1rem'}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            <label style={{fontWeight:600,color:'#34574A'}}>Descrição</label>
            <textarea value={descricao} onChange={e=>setDescricao(e.target.value)} style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:6,fontSize:'1rem',minHeight:60}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            <label style={{fontWeight:600,color:'#34574A'}}>Preço</label>
            <input type="number" value={preco} onChange={e=>setPreco(e.target.value)} required style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:6,fontSize:'1rem'}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            <label style={{fontWeight:600,color:'#34574A'}}>Promoção</label>
            <input type="number" value={promo} onChange={e=>setPromo(e.target.value)} style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:6,fontSize:'1rem'}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            <label style={{fontWeight:600,color:'#34574A'}}>Duração (dias)</label>
            <input type="number" value={duracao} onChange={e=>setDuracao(e.target.value)} required style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:6,fontSize:'1rem'}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            <label style={{fontWeight:600,color:'#34574A'}}>Slug</label>
            <input value={slug} onChange={e=>setSlug(e.target.value)} style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:6,fontSize:'1rem'}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            <label style={{fontWeight:600,color:'#34574A'}}>Quantidade</label>
            <input type="number" value={quantidade} onChange={e=>setQuantidade(e.target.value)} style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:6,fontSize:'1rem'}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            <label style={{fontWeight:600,color:'#34574A'}}>URL</label>
            <input value={url} onChange={e=>setUrl(e.target.value)} style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:6,fontSize:'1rem'}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            <label style={{fontWeight:600,color:'#34574A'}}>Stripe ID</label>
            <input value={stripeid} onChange={e=>setStripeid(e.target.value)} style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:6,fontSize:'1rem'}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            <label style={{fontWeight:600,color:'#34574A'}}>Moloni ID</label>
            <input value={moloniid} onChange={e=>setMoloniid(e.target.value)} style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:6,fontSize:'1rem'}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            <label style={{fontWeight:600,color:'#34574A'}}>Ordem</label>
            <input type="number" value={ordem} onChange={e=>setOrdem(e.target.value)} style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:6,fontSize:'1rem'}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            <label style={{fontWeight:600,color:'#34574A'}}>Destacar</label>
            <select value={destacar} onChange={e=>setDestacar(Number(e.target.value))} style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:6,fontSize:'1rem'}}>
              <option value={0}>Não</option>
              <option value={1}>Sim</option>
            </select>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            <label style={{fontWeight:600,color:'#34574A'}}>Destaque</label>
            <select value={destaque} onChange={e=>setDestaque(Number(e.target.value))} style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:6,fontSize:'1rem'}}>
              <option value={0}>Não</option>
              <option value={1}>Sim</option>
            </select>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            <label style={{fontWeight:600,color:'#34574A'}}>Tipo de Plano</label>
            <select value={tipo} onChange={e=>setTipo(e.target.value)} required style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:6,fontSize:'1rem'}}>
              <option value="">Selecione...</option>
              {tiposPlanos.map(tp => (
                <option key={tp.codigoTIPOPLANO} value={tp.codigoTIPOPLANO}>{tp.planoTIPOPLANO}</option>
              ))}
            </select>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6, gridColumn:'1/3'}}>
            <label style={{fontWeight:600,color:'#34574A'}}>Descritivos do Plano</label>
            {descritivos.map((d, idx) => (
              <div
                key={idx}
                onDragOver={(e) => handleDragOver(idx, e)}
                onDrop={() => handleDrop(idx)}
                style={{
                  display:'flex',
                  gap:8,
                  alignItems:'center',
                  marginBottom:4,
                  border: dragIndex === idx ? '1.5px dashed #ba824f' : '1px solid transparent',
                  borderRadius: 6,
                  padding: 2,
                  background: dragIndex === idx ? '#fff7ed' : 'transparent'
                }}
              >
                <span
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  title="Arraste para reordenar"
                  aria-label="Arraste para reordenar"
                  style={{
                    cursor: 'grab',
                    userSelect: 'none',
                    padding: '6px 8px',
                    color: '#ba824f',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ≡≡
                </span>
                <input value={d} onChange={e=>handleChangeDescritivo(idx, e.target.value)} style={{flex:1,padding:'8px 12px',border:'1px solid #ddd',borderRadius:6,fontSize:'1rem'}} placeholder={`Descritivo ${idx+1}`}/>
                <button type="button" style={{background:'#c62828',color:'#fff',border:'none',borderRadius:6,padding:'6px 10px',cursor:'pointer'}} onClick={()=>handleRemoveDescritivo(idx)}>Remover</button>
              </div>
            ))}
            <button type="button" style={{background:'#34574A',color:'#fff',border:'none',borderRadius:6,padding:'8px 16px',fontWeight:600,marginTop:6,cursor:'pointer',alignSelf:'flex-start'}} onClick={handleAddDescritivo}>Adicionar Descritivo</button>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:12,gridColumn:'1/3'}}>
            <label style={{fontWeight:600,color:'#34574A'}}>Status</label>
            <StatusButton type="button" $ativo={ativo} onClick={()=>setAtivo(true)}>Ativo</StatusButton>
            <StatusButton type="button" $ativo={!ativo} onClick={()=>setAtivo(false)}>Inativo</StatusButton>
          </div>
          <div style={{display:'flex',justifyContent:'flex-end',gap:12,gridColumn:'1/3'}}>
            <button type="button" style={{background:'#bbb',color:'#222',border:'none',borderRadius:8,padding:'10px 18px',fontSize:'1rem',fontWeight:600,cursor:'pointer'}} onClick={onCancel}>Cancelar</button>
            <button type="submit" style={{background:'#34574A',color:'#fff',border:'none',borderRadius:8,padding:'10px 18px',fontSize:'1rem',fontWeight:600,cursor:'pointer'}}>Salvar</button>
          </div>
        </form>
      </TabContent>

      {/* Tab Content: Cupons */}
      <TabContent $active={activeTab === 'cupons'}>
        <CouponManager planoId={planoId} cupons={initialData?.cupons} onCouponChange={onCouponChange} />
      </TabContent>
    </div>
  );
};

const Planos = () => {
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [tiposPlanos, setTiposPlanos] = useState([]);
  const [activeTab, setActiveTab] = useState('plano');
  const { currencySymbol, exchangeRate, currency } = useCurrency();
  // Filtro por tipo de plano ('' = todos)
  const [typeFilter, setTypeFilter] = useState('');
  const porPagina = 6;

  const formatPrice = (value) => {
    const base = Number(value || 0);
    const rate = Number(exchangeRate) || 1;
    const converted = Number.isFinite(base)
      ? (Math.round((base * rate + Number.EPSILON) * 100) / 100)
      : 0;
    const symbol = currencySymbol || currency || '€';
    return `${symbol} ${converted.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  // Aplica filtro por tipo quando selecionado
  const planosFiltrados = planos.filter(p => {
    if (typeFilter) {
      const norm = (s) => (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const tipoNome = p.tipoPlano?.planoTIPOPLANO || '';
      if (norm(tipoNome) !== norm(typeFilter)) return false;
    }
    return true;
  });
  const totalPaginas = Math.ceil(planosFiltrados.length / porPagina);
  // Ordena primeiro por tipoPLANO, depois por ordemPLANO
  const planosOrdenados = [...planosFiltrados].sort((a, b) => {
    // Primeiro compara tipoPLANO
    if (a.tipoPLANO < b.tipoPLANO) return -1;
    if (a.tipoPLANO > b.tipoPLANO) return 1;
    // Se tipoPLANO for igual, compara ordemPLANO (nulo/undefined vai para o fim)
    const ordemA = a.ordemPLANO ?? 9999;
    const ordemB = b.ordemPLANO ?? 9999;
    return ordemA - ordemB;
  });
  const paginados = planosOrdenados.slice((pagina - 1) * porPagina, pagina * porPagina);

  const fetchPlanos = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('auth_token');
    try {
      const res = await fetch(`${import.meta.env.VITE_URL_API}/planos`, {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      const mapped = Array.isArray(data)
        ? data.map(d => ({
            ...d,
            descricaoPLANO: d.descricaoPLANO ?? d.descricao ?? d.description ?? (Array.isArray(d.descritivosPLANO) ? d.descritivosPLANO.join(', ') : ''),
          }))
        : [];
      setPlanos(mapped);
    } catch {
      setError('Erro ao buscar planos.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPlanos();
    // Buscar tipos de planos
    const fetchTipos = async () => {
      const token = localStorage.getItem('auth_token');
      try {
        const res = await fetch(`${import.meta.env.VITE_URL_API}/tipos-planos`, {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await res.json();
        setTiposPlanos(Array.isArray(data) ? data : []);
      } catch {
        setTiposPlanos([]);
      }
    };
    fetchTipos();
  }, []);

  const handleAdd = () => {
    setEditData(null);
    setActiveTab('plano');
    setShowForm(true);
  };

  const handleEdit = async (item) => {
    setActiveTab('plano');
    // Busca os dados completos do plano pelo ID antes de editar
    const token = localStorage.getItem('auth_token');
    try {
      const res = await fetch(`${import.meta.env.VITE_URL_API}/planos/${item.codigoPLANO}`, {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      const normalized = {
        ...data,
        descricaoPLANO: data?.descricaoPLANO ?? data?.descricao ?? data?.description ?? (Array.isArray(data?.descritivosPLANO) ? data.descritivosPLANO.join(', ') : ''),
      };
      setEditData(normalized);
      setShowForm(true);
    } catch {
      setEditData(item);
      setShowForm(true);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditData(null);
  };

  const handleFormSubmit = async (formData) => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    try {
      if (editData) {


        

        const payload = {
            nomePLANO: formData.nome || editData.nomePLANO,
            descricaoPLANO: formData.descricao || editData.descricaoPLANO,
            descricao: formData.descricao || editData.descricao,
            ativoPLANO: formData.ativo !== undefined ? formData.ativo : editData.ativoPLANO,
            tipoPLANO: formData.tipo || editData.tipoPLANO,
            slugPLANO: formData.slug || editData.slugPLANO,
            quantidadePLANO: formData.quantidade ? Number(formData.quantidade) : editData.quantidadePLANO,
            urlPLANO: formData.url || editData.urlPLANO,
            valorPLANO: formData.preco !== undefined ? Number(formData.preco) : Number(editData.valorPLANO),
            promoPLANO: formData.promo !== undefined ? Number(formData.promo) : (Number(editData.promoPLANO) || 0),
            tempoPLANO: formData.duracao ? Number(formData.duracao) : editData.tempoPLANO,
            destacarPLANO: formData.destacar !== undefined ? Number(formData.destacar) : editData.destacarPLANO,
            destaquePLANO: formData.destaque !== undefined ? Number(formData.destaque) : editData.destaquePLANO,
            stripeidPLANO: formData.stripeid || editData.stripeidPLANO,
            moloniidPLANO: formData.moloniid || editData.moloniidPLANO,
            ordemPLANO: formData.ordem ? Number(formData.ordem) : editData.ordemPLANO,
            descritivosPLANO: Array.isArray(formData.descritivosPLANO) ? formData.descritivosPLANO : (editData.descritivosPLANO || [])
          };
          await fetch(`${import.meta.env.VITE_URL_API}/planos/${editData.codigoPLANO}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });
        Swal.fire('Sucesso', 'Plano atualizado com sucesso!', 'success');
        fetchPlanos();
      } else {
        const payloadPost = {
            nomePLANO: formData.nome || '',
            descricaoPLANO: formData.descricao || '',
            descricao: formData.descricao || '',
            ativoPLANO: formData.ativo !== undefined ? formData.ativo : true,
            tipoPLANO: formData.tipo || '',
            slugPLANO: formData.slug || '',
            quantidadePLANO: formData.quantidade ? Number(formData.quantidade) : 0,
            urlPLANO: formData.url || '',
            valorPLANO: formData.preco !== undefined ? Number(formData.preco) : 0,
            promoPLANO: formData.promo !== undefined ? Number(formData.promo) : 0,
            tempoPLANO: formData.duracao ? Number(formData.duracao) : 0,
            destacarPLANO: formData.destacar !== undefined ? Number(formData.destacar) : 0,
            destaquePLANO: formData.destaque !== undefined ? Number(formData.destaque) : 0,
            stripeidPLANO: formData.stripeid || '',
            moloniidPLANO: formData.moloniid || '',
            ordemPLANO: formData.ordem ? Number(formData.ordem) : 0,
            descritivosPLANO: Array.isArray(formData.descritivosPLANO) ? formData.descritivosPLANO : []
          };
          await fetch(`${import.meta.env.VITE_URL_API}/planos`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payloadPost)
        });
        Swal.fire('Sucesso', 'Plano criado com sucesso!', 'success');
        fetchPlanos();
      }
      setShowForm(false);
      setEditData(null);
      fetchPlanos();
    } catch {
      Swal.fire('Erro', 'Não foi possível salvar o plano.', 'error');
    }
  };

  const handleDelete = (item) => {
    Swal.fire({
      title: 'Tem certeza?',
      text: `Deseja realmente excluir o plano "${item.nomePLANO}"? Esta ação não poderá ser desfeita!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#c62828',
      cancelButtonColor: '#34574A',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {
        const token = localStorage.getItem('auth_token');
        if (!token) return;
        try {
          await fetch(`${import.meta.env.VITE_URL_API}/planos/${item.codigoPLANO}`, {
            method: 'DELETE',
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
          setTimeout(fetchPlanos, 400);
          Swal.fire('Sucesso', 'Plano excluído com sucesso!', 'success');
        } catch {
          Swal.fire('Erro', 'Não foi possível excluir o plano.', 'error');
        }
      }
    });
  };

  return (
    <PageContainer style={{ background: '#f5f5f5' }}>
      <HeaderRow>
        <h1 style={{margin:0}}>Planos</h1>
        <AddButton onClick={handleAdd}><FaPlus /> Novo Plano</AddButton>
      </HeaderRow>
      {/* Filtro por tipo de plano (Particular, Profissional, Férias, Destaque) */}
      <div style={{ display: 'flex', gap: 10, margin: '14px 0 8px 0', flexWrap: 'wrap' }}>
        {[{label:'Todos', value:''}, {label:'Particular', value:'Particular'}, {label:'Profissional', value:'Profissional'}, {label:'Férias', value:'Férias'}, {label:'Destaque', value:'Destaque'}].map(opt => (
          <button
            key={opt.label}
            type="button"
            onClick={() => { setTypeFilter(opt.value); setPagina(1); }}
            style={{
              padding: '8px 18px',
              borderRadius: 20,
              border: typeFilter === opt.value ? '2px solid #ba824f' : '1px solid #e0b07c',
              background: typeFilter === opt.value ? '#ba824f' : '#fff',
              color: typeFilter === opt.value ? '#fff' : '#ba824f',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '.95rem',
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Nome</th>
              <th>Descrição</th>
              <th>Preço</th>
              <th>Promoção</th>
              <th>Duração</th>
              <th>Tipo</th>
              <th>Ordem</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {paginados.map(item => (
              <tr key={item.codigoPLANO}>
                <td>{item.codigoPLANO}</td>
                <td dangerouslySetInnerHTML={{__html: item.nomePLANO}} />
                <td>{item.descricaoPLANO || item.descricao || item.description || (Array.isArray(item.descritivosPLANO) ? item.descritivosPLANO.join(', ') : '-') || '-'}</td>
                <td className="price">{formatPrice(item.valorPLANO ?? item.precoPLANO ?? 0)}</td>
                <td style={{color: item.promoPLANO && item.promoPLANO > 0 ? '#ba824f' : '#333', fontWeight: item.promoPLANO && item.promoPLANO > 0 ? '600' : 'normal'}}>
                  {item.promoPLANO && item.promoPLANO > 0 ? formatPrice(item.promoPLANO) : '-'}
                </td>
                <td>{item.tempoPLANO ?? item.duracaoPLANO} dias</td>
                <td>{item.tipoPlano?.planoTIPOPLANO || '-'}</td>
                <td>{item.ordemPLANO ?? '-'}</td>
                <td>
                  <StatusButton
                    $ativo={item.ativoPLANO === true || item.ativoPLANO === 1}
                    onClick={async () => {
                      const token = localStorage.getItem('auth_token');
                      if (!token) return;
                      try {
                        await fetch(`${import.meta.env.VITE_URL_API}/planos/${item.codigoPLANO}`, {
                          method: 'PATCH',
                          headers: {
                            'accept': 'application/json',
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({
                            ...item,
                            ativoPLANO: !(item.ativoPLANO === true || item.ativoPLANO === 1)
                          })
                        });
                        setTimeout(fetchPlanos, 400);
                      } catch {
                        Swal.fire('Erro', 'Não foi possível atualizar o status.', 'error');
                      }
                    }}
                    title={item.ativoPLANO === true || item.ativoPLANO === 1 ? 'Desativar' : 'Ativar'}
                  >
                    {item.ativoPLANO === true || item.ativoPLANO === 1 ? <FaCheck /> : <FaTimes />} {item.ativoPLANO === true || item.ativoPLANO === 1 ? 'Ativo' : 'Inativo'}
                  </StatusButton>
                </td>
                <ActionCell>
                  <IconButton title="Editar" onClick={()=>handleEdit(item)}><FaEdit /></IconButton>
                  <IconButton title="Excluir" onClick={()=>handleDelete(item)}><FaTrashAlt /></IconButton>
                </ActionCell>
              </tr>
            ))}
          </tbody>
        </Table>
        {/* Paginação */}
        <div style={{display:'flex',justifyContent:'center',alignItems:'center',gap:8,margin:'24px 0'}}>
          <button onClick={()=>setPagina(p=>Math.max(1,p-1))} disabled={pagina===1} style={{padding:'6px 14px',borderRadius:6,border:'1px solid #eee',background:'#fff',cursor:pagina===1?'not-allowed':'pointer'}}>Anterior</button>
          {[...Array(Math.min(totalPaginas, 10))].map((_,i)=>(
            <button
              key={i}
              onClick={()=>setPagina(i+1)}
              style={{padding:'6px 12px',borderRadius:6,border:'1.5px solid '+(pagina===i+1?'#34574A':'#eee'),background:pagina===i+1?'#34574A':'#fff',color:pagina===i+1?'#fff':'#222',fontWeight:600,cursor:'pointer'}}
            >{i+1}</button>
          ))}
          <button onClick={()=>setPagina(p=>Math.min(totalPaginas,p+1))} disabled={pagina===totalPaginas} style={{padding:'6px 14px',borderRadius:6,border:'1px solid #eee',background:'#fff',cursor:pagina===totalPaginas?'not-allowed':'pointer'}}>Próxima</button>
        </div>
        {showForm && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.25)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
            }}
            onClick={handleFormCancel}
          >
            <div
              style={{
                background: '#fff',
                padding: 32,
                borderRadius: 12,
                minWidth: 680,
                maxWidth: 900,
                width: '90vw',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
              }}
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={handleFormCancel}
                style={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  background: 'transparent',
                  border: 'none',
                  fontSize: 28,
                  color: '#888',
                  cursor: 'pointer',
                  zIndex: 10,
                }}
                aria-label="Fechar"
              >&#10005;</button>
              <h2 style={{ marginBottom: 16 }}>{editData ? 'Editar Plano' : 'Novo Plano'}</h2>
              <PlanoForm 
                onSubmit={handleFormSubmit} 
                onCancel={handleFormCancel} 
                initialData={editData} 
                tiposPlanos={tiposPlanos} 
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                planoId={editData?.codigoPLANO}
                onCouponChange={fetchPlanos}
              />
            </div>
          </div>
        )}
      </TableWrapper>
    </PageContainer>
  );
};

export default Planos;
